import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';

// Playback speeds, expressed as "data time covered per wall-clock second".
// The spacing between hits always stays proportional to the real elapsed time
// between reads — speed only changes how fast that real timeline is replayed.
const SPEEDS = [
  { label: '30 min / sec', perSec: 30 * 60000 },
  { label: '1 hr / sec', perSec: 60 * 60000 },
  { label: '3 hr / sec', perSec: 180 * 60000 },
  { label: '6 hr / sec', perSec: 360 * 60000 },
  { label: '12 hr / sec', perSec: 720 * 60000 },
  { label: '1 day / sec', perSec: 1440 * 60000 },
];

// In "skip idle time" mode, any gap between two hits longer than this much real
// time is collapsed to this length on the playback timeline — so actual movement
// stays to-scale, but long parks/overnights fast-forward instead of stalling.
const IDLE_CAP_MS = 30 * 60000;

// Wall-clock time the replay pauses on each hit so you can register where the
// vehicle jumped to before it moves on.
const DWELL_MS = 1000;

function fmtClock(ts) {
  if (ts == null) return '—';
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtShort(ts) {
  if (ts == null) return '';
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function LPRMapReplay({ timeline }) {
  // Flatten the timeline into a single GPS-bearing, time-ordered read list.
  const allReads = useMemo(() => {
    const reads = [];
    for (const dayReads of Object.values(timeline || {})) {
      for (const r of dayReads) {
        if (r.ts != null && r.lat != null && r.lng != null) {
          reads.push({ ts: r.ts, lat: r.lat, lng: r.lng, camera: r.camera || r.site || 'Unknown', direction: r.direction });
        }
      }
    }
    reads.sort((a, b) => a.ts - b.ts);
    return reads;
  }, [timeline]);

  const minTs = allReads.length ? allReads[0].ts : 0;
  const maxTs = allReads.length ? allReads[allReads.length - 1].ts : 0;

  const [windowRange, setWindowRange] = useState([minTs, maxTs]);
  // `clock` is a position on the PLAYBACK timeline (0..warp.duration), not a real
  // timestamp — warp.toReal() maps it back to the actual time for display.
  const [clock, setClock] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const [skipIdle, setSkipIdle] = useState(true);

  // Reset the window whenever a new dataset arrives.
  useEffect(() => {
    setWindowRange([minTs, maxTs]);
    setClock(0);
    setPlaying(false);
  }, [minTs, maxTs]);

  const [startTs, endTs] = windowRange;

  const windowReads = useMemo(
    () => allReads.filter(r => r.ts >= startTs && r.ts <= endTs),
    [allReads, startTs, endTs]
  );

  // Build the playback-time ↔ real-time warp for the current window. With idle
  // skipping on, gaps longer than IDLE_CAP_MS contribute only IDLE_CAP_MS to the
  // playback duration, so the clock fast-forwards across dead time.
  const warp = useMemo(() => {
    const reads = windowReads;
    const n = reads.length;
    if (n === 0) return { duration: 0, toReal: () => startTs, toPlay: () => 0, bounds: [] };
    if (n === 1) return { duration: 0, toReal: () => reads[0].ts, toPlay: () => 0, bounds: [0] };
    const cum = [0];
    const gaps = [];
    const eff = [];
    for (let i = 0; i < n - 1; i++) {
      const g = Math.max(0, reads[i + 1].ts - reads[i].ts);
      const e = skipIdle ? Math.min(g, IDLE_CAP_MS) : g;
      gaps.push(g); eff.push(e);
      cum.push(cum[i] + e);
    }
    const duration = cum[n - 1];
    const toReal = (p) => {
      if (p <= 0) return reads[0].ts;
      if (p >= duration) return reads[n - 1].ts;
      let i = 0;
      while (i < n - 1 && cum[i + 1] <= p) i++;
      if (eff[i] <= 0) return reads[i].ts;
      return reads[i].ts + ((p - cum[i]) / eff[i]) * gaps[i];
    };
    const toPlay = (realTs) => {
      if (realTs <= reads[0].ts) return 0;
      if (realTs >= reads[n - 1].ts) return duration;
      let i = 0;
      while (i < n - 1 && reads[i + 1].ts <= realTs) i++;
      if (gaps[i] <= 0) return cum[i];
      return cum[i] + ((realTs - reads[i].ts) / gaps[i]) * eff[i];
    };
    // `cum` holds the playback position where each read is reached — the loop
    // snaps to and pauses on these so every hit gets a beat.
    return { duration, toReal, toPlay, bounds: cum };
  }, [windowReads, skipIdle, startTs]);

  // Map + layer refs (imperatively driven so animation never thrashes React).
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const trailRef = useRef(null);
  const curMarkerRef = useRef(null);
  const camLayerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  // Animation refs
  const rafRef = useRef(null);
  const lastFrameRef = useRef(0);
  const clockRef = useRef(0);
  const lastPushRef = useRef(0);
  const lastCenteredRef = useRef(null);
  const dwellUntilRef = useRef(0);

  // ── Create the map once ──────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    // A view (center + zoom) must be set before the tile layer is added, otherwise
    // Leaflet tries to fill an undefined viewport ("infinite tiles"). fitBounds in
    // the window effect refines this to the actual data immediately after.
    if (allReads.length) map.setView([allReads[0].lat, allReads[0].lng], 11);
    else map.setView([39.5, -98.35], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    trailRef.current = L.polyline([], { color: '#6d28d9', weight: 3, opacity: 0.85 }).addTo(map);
    curMarkerRef.current = L.circleMarker([0, 0], {
      radius: 9, color: '#fff', weight: 3, fillColor: '#dc2626', fillOpacity: 1, opacity: 0,
    }).addTo(map);
    camLayerRef.current = L.layerGroup().addTo(map);

    mapRef.current = map;
    setMapReady(true);
    // Recalculate size once layout settles. Guard against the StrictMode
    // double-mount where this fires after the map was already removed.
    const sizeTimer = setTimeout(() => {
      if (mapRef.current === map) map.invalidateSize(false);
    }, 60);

    return () => {
      clearTimeout(sizeTimer);
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Plot the (static) camera dots for the selected window + fit bounds ─────
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const layer = camLayerRef.current;
    layer.clearLayers();

    // Dedupe cameras by rounded coordinate so repeat hits don't stack markers.
    const seen = new Map();
    for (const r of windowReads) {
      const key = `${r.lat.toFixed(5)},${r.lng.toFixed(5)}`;
      if (!seen.has(key)) seen.set(key, r);
    }
    for (const r of seen.values()) {
      L.circleMarker([r.lat, r.lng], {
        radius: 7, color: '#fff', weight: 2, fillColor: '#2563eb', fillOpacity: 0.95,
      }).bindTooltip(r.camera, { direction: 'top' }).addTo(layer);
    }

    if (windowReads.length) {
      // Start zoomed in on the first hit of the window (the replay then pans to
      // each new hit at this zoom). Users can zoom out to see the whole spread.
      mapRef.current.setView([windowReads[0].lat, windowReads[0].lng], 13);
    }
    // Reset the playhead to the start of the playback timeline.
    clockRef.current = 0;
    lastCenteredRef.current = null;
    setClock(0);
    setPlaying(false);
  }, [windowReads, mapReady, startTs]);

  // ── Draw the trail + current position for a given PLAYBACK position ─────────
  const renderAt = useCallback((p) => {
    if (!trailRef.current || !curMarkerRef.current) return;
    const realTs = warp.toReal(p);
    const seen = windowReads.filter(r => r.ts <= realTs);
    trailRef.current.setLatLngs(seen.map(r => [r.lat, r.lng]));
    if (seen.length) {
      const last = seen[seen.length - 1];
      curMarkerRef.current.setLatLng([last.lat, last.lng]);
      curMarkerRef.current.setStyle({ opacity: 1 });
      // Keep the most recent hit centered, but only re-pan when the playhead
      // actually reaches a new read (not every animation frame) so it stays smooth.
      if (mapRef.current && lastCenteredRef.current !== last.ts) {
        lastCenteredRef.current = last.ts;
        mapRef.current.panTo([last.lat, last.lng], { animate: true, duration: 0.5 });
      }
    } else {
      curMarkerRef.current.setStyle({ opacity: 0 });
      lastCenteredRef.current = null;
    }
  }, [windowReads, warp]);

  useEffect(() => { renderAt(clock); }, [clock, renderAt]);

  // ── Animation loop ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    lastFrameRef.current = performance.now();
    dwellUntilRef.current = 0;
    const perSec = SPEEDS[speedIdx].perSec;
    const bounds = warp.bounds;

    // Smallest read-position strictly after p (the next hit to pause on).
    const nextBoundary = (p) => {
      for (let i = 0; i < bounds.length; i++) if (bounds[i] > p + 1) return bounds[i];
      return null;
    };

    const tick = (now) => {
      // Holding on a hit — keep the frame clock fresh so we don't lurch on resume.
      if (now < dwellUntilRef.current) {
        lastFrameRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const dt = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;
      let next = clockRef.current + perSec * dt;

      // If we'd pass the next hit this frame, stop exactly on it and pause.
      const b = nextBoundary(clockRef.current);
      if (b != null && next >= b) {
        next = b;
        dwellUntilRef.current = now + DWELL_MS;
      }

      if (next >= warp.duration) {
        clockRef.current = warp.duration;
        setClock(warp.duration);
        setPlaying(false);
        return;
      }
      clockRef.current = next;
      // Push to React on a snap (so the marker lands) or ~20/sec otherwise.
      if (dwellUntilRef.current > now || now - lastPushRef.current > 50) {
        lastPushRef.current = now;
        setClock(next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, speedIdx, warp.duration, warp.bounds]);

  function togglePlay() {
    if (!windowReads.length) return;
    if (!playing && clockRef.current >= warp.duration) {
      // Restart from the beginning of the playback timeline.
      clockRef.current = 0;
      setClock(0);
    }
    setPlaying(p => !p);
  }

  function onScrub(_, val) {
    setPlaying(false);
    dwellUntilRef.current = 0;
    clockRef.current = val;
    setClock(val);
  }

  const realClock = warp.toReal(clock);
  const seenCount = useMemo(() => windowReads.filter(r => r.ts <= realClock).length, [windowReads, realClock]);
  const currentRead = useMemo(() => {
    const seen = windowReads.filter(r => r.ts <= realClock);
    return seen.length ? seen[seen.length - 1] : null;
  }, [windowReads, realClock]);

  const noGps = allReads.length === 0;

  const btn = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer',
    color: '#6d28d9', fontWeight: 700, fontSize: '13px', borderRadius: '8px',
    padding: '8px 14px', fontFamily: 'inherit',
  };

  return (
    <Box>
      {noGps ? (
        <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', p: '14px 16px' }}>
          <Typography sx={{ fontSize: '13px', color: '#92400e' }}>
            No GPS coordinates were found in this file's reads, so a map replay can't be drawn. (Some
            mobile / trailer cameras report no fixed location.)
          </Typography>
        </Box>
      ) : (
        <>
          {/* Readout */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{
              bgcolor: '#4c1d95', color: '#fff', borderRadius: '10px', px: '14px', py: '8px',
              fontFamily: "'Courier New', monospace", fontWeight: 700, fontSize: '14px',
            }}>
              {fmtClock(realClock)}
            </Box>
            <Box sx={{ flex: 1, minWidth: '180px' }}>
              <Typography sx={{ fontSize: '13px', color: '#0f172a', fontWeight: 600, lineHeight: 1.3 }}>
                {currentRead ? currentRead.camera : 'Press play to begin replay'}
                {currentRead?.direction && (
                  <Box component="span" sx={{
                    display: 'inline-block', fontSize: '10px', bgcolor: '#f1f5f9', color: '#475569',
                    px: '6px', py: '1px', borderRadius: '4px', ml: '6px', fontWeight: 600, verticalAlign: 'middle',
                  }}>{currentRead.direction}</Box>
                )}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: '2px' }}>
                {seenCount} of {windowReads.length} reads shown
              </Typography>
            </Box>
          </Box>

          {/* Map */}
          <Box
            ref={containerRef}
            sx={{ height: 420, width: '100%', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', mb: 1.5, zIndex: 0 }}
          />

          {/* Transport controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Box component="button" type="button" onClick={togglePlay}
              sx={{ ...btn, bgcolor: '#6d28d9', color: '#fff', border: 'none', '&:hover': { bgcolor: '#5b21b6' } }}>
              {playing ? (
                <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>Pause</>
              ) : (
                <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>Play</>
              )}
            </Box>
            <Box component="button" type="button"
              onClick={() => { setPlaying(false); clockRef.current = 0; setClock(0); }}
              sx={{ ...btn, '&:hover': { bgcolor: '#f5f3ff' } }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="11 19 2 12 11 5"/><polyline points="22 19 13 12 22 5"/></svg>
              Restart
            </Box>

            <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}>
              <Box component="input" type="checkbox" checked={skipIdle}
                onChange={e => { setPlaying(false); clockRef.current = 0; setClock(0); setSkipIdle(e.target.checked); }}
                sx={{ width: 15, height: 15, accentColor: '#6d28d9', cursor: 'pointer' }} />
              <Typography sx={{ fontSize: '12.5px', color: '#475569', fontWeight: 600 }}>Skip idle time</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ml: 'auto' }}>
              <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Speed</Typography>
              <Box component="select" value={speedIdx}
                onChange={e => setSpeedIdx(Number(e.target.value))}
                sx={{
                  fontSize: '13px', fontFamily: 'inherit', color: '#1e293b', fontWeight: 600,
                  border: '1px solid #cbd5e1', borderRadius: '8px', px: '8px', py: '6px', bgcolor: '#fff', cursor: 'pointer',
                }}>
                {SPEEDS.map((s, i) => <option key={s.label} value={i}>{s.label}</option>)}
              </Box>
            </Box>
          </Box>

          {/* Scrubber (playback timeline; labels show real time) */}
          <Box sx={{ px: '4px' }}>
            <Slider
              value={Math.min(Math.max(clock, 0), warp.duration)}
              min={0} max={warp.duration || 1}
              onChange={onScrub}
              valueLabelDisplay="auto"
              valueLabelFormat={p => fmtShort(warp.toReal(p))}
              sx={{ color: '#6d28d9' }}
            />
          </Box>

          {/* Window (portion) selector */}
          <Box sx={{ bgcolor: '#faf9ff', border: '1px solid #ede9fe', borderRadius: '10px', p: '12px 16px 4px', mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#475569' }}>
                Replay window — drag to visualize the whole history or just a portion
              </Typography>
              <Box sx={{
                fontSize: '12px', fontWeight: 700, color: '#6d28d9',
                bgcolor: '#f5f3ff', px: '10px', py: '3px', borderRadius: '99px', flexShrink: 0,
              }}>
                {fmtShort(startTs)} – {fmtShort(endTs)}
              </Box>
            </Box>
            <Slider
              value={windowRange}
              min={minTs} max={maxTs}
              onChange={(_, val) => setWindowRange(val)}
              valueLabelDisplay="auto"
              valueLabelFormat={fmtShort}
              sx={{ color: '#a78bfa' }}
            />
          </Box>

          <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 1.25, lineHeight: 1.5 }}>
            With <strong>Skip idle time</strong> on, actual movement plays to real-time scale while long
            parks/overnights fast-forward (the clock still shows true timestamps); turn it off for a strictly
            linear, to-scale replay. Map tiles load from OpenStreetMap (only the map area you view is
            requested — never any plate, location, or case data); all parsing and animation stay in your browser.
          </Typography>
        </>
      )}
    </Box>
  );
}
